import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { Loading, LoadingContainer } from "go-core/components/Loading";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import FormatResourceStatus from "../../../../../../../../../components/Common/Formatters/FormatResourceStatus/FormatResourceStatus";
import RenderHeaderDate from "../../../../../../../../../components/Common/Renderers/RenderHeaderDate";
import { organizationApi } from "../../../../../../../../../services/Api/Organization/organizationApi";
import { PaymentSourceApi } from "../../../../../../../../../services/Api/Organization/organizationTypes";
import { SubscriptionInvoiceApi } from "../../../../../../../../../services/Api/Subscription/subscriptionTypes";
import { selectOrganization } from "../../../../../../../../../services/organizations/selectors";
import { getApiURL } from "../../../../../../../../../utils/envutil";
import PaymentModal from "../../../components/Payment/PaymentModal";
import ProformaPreviewPaymentMethodCard from "./ProformaPreviewPaymentMethodCard";

interface Props {
	proformaId: string;
	styles?: Record<string, any>;
	onClose: () => void;
	setErrors?: (errors: ApiError[]) => void;
	setObservedInvoice: (invoice: string) => void;
	setStartCheckingPaymentStatus: (value: boolean) => void;
	fetchInvoices: () => Promise<SubscriptionInvoiceApi[] | undefined>;
	closePreview: () => void;
	cardPaymentInProgress: boolean;
	loadingItems?: boolean;
}

const ProformaPreview = ({
	proformaId,
	styles,
	setErrors,
	onClose,
	setObservedInvoice,
	setStartCheckingPaymentStatus,
	fetchInvoices,
	cardPaymentInProgress,
	closePreview,
	loadingItems,
}: Props) => {
	const [loading, setLoading] = useState<boolean>(false);
	const organization = useSelector(selectOrganization);
	const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
	const [automaticallyPayingAfterAddingCard, setAutomaticallyPayingAfterAddingCard] = useState<boolean>(false);
	const location = useLocation();
	const cardLoading = location.pathname.includes("card_loading");
	const [loadingCreditCard, setLoadingCreditCard] = useState<boolean>(false);
	const [fetchingCreditCardAfterAdd, setFetchingCreditCardAfterAdd] = useState<boolean>(false);
	const [loadingPaymentSource, setLoadingPaymentSource] = useState<boolean>(false);
	const [proforma, setProforma] = useState<SubscriptionInvoiceApi | undefined>(undefined);
	const [paymentSource, setPaymentSource] = useState<PaymentSourceApi | undefined>(undefined);
	const [paymentAction, setPaymentAction] = useState<"ADD_CARD" | "PAY_BY_CARD" | "PAY_ONLINE">(
		paymentSource ? "PAY_BY_CARD" : "ADD_CARD"
	);
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);
	const cardChecking = useRef(cardLoading);
	const afterAddingCardPaymentChecking = useRef(false);
	const afterCardPayment = useRef(false);
	const history = useHistory();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const [showPaymentModal, setShowPaymentModal] = useState(false);

	useEffect(() => {
		return () => {
			handleChangeTabTitle(t("modules.invoice.header.title"));
		};
	}, []);

	useEffect(() => {
		initProforma();
	}, [proformaId]);

	const initProforma = async () => {
		await fetchProforma();
		if (proformaId) {
			await fetchCreditCard();
		}
	};

	useEffect(() => {
		if (cardPaymentInProgress) {
			afterCardPayment.current = true;
		} else if (afterCardPayment.current) closePreview();
	}, [cardPaymentInProgress]);

	useEffect(() => {
		document.addEventListener("keydown", handleHideDropdown, true);
		document.addEventListener("click", handleClickOutside, true);
		return () => {
			document.removeEventListener("keydown", handleHideDropdown, true);
			document.removeEventListener("click", handleClickOutside, true);
		};
	});

	useEffect(() => {
		if (paymentSource) setPaymentAction("PAY_BY_CARD");
		else setPaymentAction("ADD_CARD");
	}, [paymentSource]);

	const fetchCreditCard = async () => {
		if (!cardChecking.current) {
			setLoadingPaymentSource(true);
		}
		try {
			const res = await organizationApi.getBillingCard();
			if (!cardChecking.current || cardLoading) {
				setPaymentSource(res);
			}
			if (["VALID", "INVALID", "DISABLED"].includes(res?.status)) {
				if (cardChecking.current) {
					setPaymentSource(res);
					afterAddingCardPaymentChecking.current = true;
				}
				cardChecking.current = false;
				setFetchingCreditCardAfterAdd(false);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoadingPaymentSource(false);
	};

	const intervalCallback = () => {
		if (cardChecking.current) {
			fetchCreditCard();
		}
		if (afterAddingCardPaymentChecking.current) {
			handleAutomaticallyPayingAfterAddingCard();
		}
	};

	const handleAutomaticallyPayingAfterAddingCard = async () => {
		try {
			const { data } = await organizationApi.getSubscriptionInvoicesPaymentStatus();
			if (data?.data?.payment_status !== "PROGRESS") {
				afterAddingCardPaymentChecking.current = false;
				setAutomaticallyPayingAfterAddingCard(false);
				await fetchInvoices();
				history.push(`/${organization.id}/subscriptions/invoices`);
			} else {
				await fetchInvoices();
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	useEffect(() => {
		const interval = setInterval(() => intervalCallback(), 3000);
		return () => {
			return clearInterval(interval);
		};
	}, [afterAddingCardPaymentChecking, cardChecking]);

	const fetchProforma = async () => {
		setLoading(true);
		try {
			if (!proforma && proformaId) {
				const params = {
					f: btoa(
						unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}proforma_id|e=${proformaId}`))
					),
				};
				const res = await organizationApi.getSubscriptionInvoices(params);
				setProforma(res[0]);
				handleChangeTabTitle(`#${res[0].proforma_id} | ${t("modules.invoice.header.title")}`);
			} else setProforma(proforma);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setLoading(false);
	};

	const handleHideDropdown = (event: KeyboardEvent) => {
		if (event.key === "Escape") {
			onClose();
		}
	};

	const handleClickOutside = (event: any) => {
		if (ref.current && !ref.current?.contains(event.target)) {
			onClose();
		}
	};

	const renderCardExpirationDate = () => {
		if (paymentSource?.status === "VALID") {
			return (
				<div className="mb-1">
					<span className="text-muted">
						<span className="text-success me-2">{t("modules.invoice.field.active_female.title")}</span>
						{t("lib:common.word.to")}: {FormatDate(paymentSource.card.expired_at).split(",")[0]}
					</span>
				</div>
			);
		}
	};

	const onPayProformaByCard = async () => {
		setPaymentLoading(true);
		try {
			const res = await organizationApi.paySubscriptionProformaByCard(proformaId);
			addSuccessFlash(t("flash.payment_started"));
			if (res.status === 200) {
				history.push(`?invoice_payment_progress=${proformaId}`);
				setStartCheckingPaymentStatus(true);
			}
		} catch (err) {
			handleError.alert(err, addFlash);
			if (setErrors) setErrors(handleException(err));
		}
		setPaymentLoading(false);
	};

	const onPayProformaOnline = async () => {
		setObservedInvoice(proformaId);
		setPaymentLoading(true);
		try {
			const res = await organizationApi.paySubscriptionProformaOnline(proformaId);
			if (res.status === 200) {
				setStartCheckingPaymentStatus(true);
				if (res.data.data.redirect_url) {
					window.location.href = res.data.data.redirect_url;
					return;
				}
			}
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setPaymentLoading(false);
	};

	const onAddCard = async () => {
		setLoadingCreditCard(true);
		setShowPaymentModal(true);
	};

	const onSubmit = () => {
		switch (paymentAction) {
			case "PAY_ONLINE":
				return onPayProformaOnline();
			case "PAY_BY_CARD":
				return onPayProformaByCard();
			case "ADD_CARD":
				return onAddCard();
		}
	};

	if (loading || loadingItems || !proforma || loadingPaymentSource) {
		return (
			<div className="list-preview" style={styles ? styles : undefined}>
				<div className={"list-preview-content"}>
					<LoadingContainer />
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="list-preview proforma-preview" ref={ref} style={styles ? styles : undefined}>
				<div className="list-preview-header ">
					<RenderHeaderDate date={proforma.proforma_created_at} />
					<div className="header-content">
						<div className="title" style={{ fontSize: "20px" }}>
							Proforma {proforma.proforma_id}
						</div>
						<div className="info">
							<span className="me-4">{FormatDate(proforma.proforma_created_at)}</span>
							{proforma.due_money && <span>{FormatMoney(proforma.due_money)}</span>}
						</div>
						<div className="info">
							<FormatResourceStatus
								status={proforma.payment_status === "OPEN" ? "NOT_PAID" : proforma.payment_status}
							/>
						</div>
					</div>
					<div className="list-preview-close">
						<RemoveSVG className="icon" onClick={onClose} />
					</div>
				</div>
				<div className="list-preview-content content-preview-body">
					{fetchingCreditCardAfterAdd || (cardChecking.current && !automaticallyPayingAfterAddingCard) ? (
						<Loading title={t("modules.invoice.field.adding_card_in_progress.title")} />
					) : automaticallyPayingAfterAddingCard ? (
						<Loading title={t("modules.invoice.field.payment_for_proformas_in_progress.title")} />
					) : cardPaymentInProgress ? (
						<Loading title={t("modules.invoice.field.payment_for_proforma_in_progress.title")} />
					) : (
						<>
							<h5 className="mb-4">{t("modules.invoice.field.choose_payment_method.title")}</h5>
							{paymentSource && paymentSource.status !== "INVALID" ? (
								<ProformaPreviewPaymentMethodCard
									id="card"
									label={t("modules.invoice.field.pay_by_card.title")}
									badgeStatus="RECOMMENDED"
									checked={paymentAction === "PAY_BY_CARD"}
									onClick={() => setPaymentAction("PAY_BY_CARD")}
									content={
										<div className="mt-4">
											<h5 className="mb-2">
												{paymentSource.card?.issuer} {paymentSource.card?.mask_number} **** ****
												****
											</h5>
											{renderCardExpirationDate()}
										</div>
									}
									description={t("modules.invoice.field.pay_by_card.description")}
								/>
							) : (
								<ProformaPreviewPaymentMethodCard
									id="add-card"
									label={t("modules.invoice.field.add_card.title")}
									badgeStatus="RECOMMENDED"
									checked={paymentAction === "ADD_CARD"}
									onClick={() => setPaymentAction("ADD_CARD")}
									description={t("modules.invoice.field.add_card.description")}
								/>
							)}
							<ProformaPreviewPaymentMethodCard
								id="blik"
								label={t("modules.invoice.field.pay_online.title")}
								checked={paymentAction === "PAY_ONLINE"}
								onClick={() => setPaymentAction("PAY_ONLINE")}
								description={t("modules.invoice.field.pay_online.description")}
							/>
							<ButtonLoading
								loading={loadingCreditCard || paymentLoading}
								onClick={onSubmit}
								className="w-100"
							>
								{t("modules.invoice.action.go_next.title")}
							</ButtonLoading>
						</>
					)}
				</div>
			</div>
			{showPaymentModal && (
				<PaymentModal
					show={showPaymentModal}
					onHide={() => {
						setShowPaymentModal(false);
						setLoadingCreditCard(false);
					}}
					redirectUrl={`${getApiURL()}/${
						organization.id
					}/subscriptions/invoices/${proformaId}/card_loading=true`}
				/>
			)}
		</>
	);
};

export default ProformaPreview;
